import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import styles from "./IconButton.module.css";

export interface IconSet {
  active: string;
  hover: string;
  disabled: string;
  disabledHover: string;
}

export type IconButtonSize = "xsmall" | "small" | "medium" | "large";

export interface IconButtonMenuItem {
  key: string;
  label: string;
  selected?: boolean;
  onSelect: () => void;
}

interface Props {
  label: string;
  icons: IconSet;
  onClick: () => void;
  disabled?: boolean;
  size?: IconButtonSize;
  badge?: string | number;
  content?: React.ReactNode;
  className?: string;
  menu?: { label: string; items: IconButtonMenuItem[] };
}

const isActivationKey = (e: React.KeyboardEvent) =>
  e.key === "Enter" || e.key === " ";

export const IconButton = ({
  label,
  icons,
  onClick,
  disabled = false,
  size = "medium",
  badge,
  content,
  className,
  menu,
}: Props) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isMenuOpen]);

  const src = disabled
    ? isHovered
      ? icons.disabledHover
      : icons.disabled
    : isHovered
      ? icons.hover
      : icons.active;

  const selectItem = (item: IconButtonMenuItem) => {
    item.onSelect();
    setIsMenuOpen(false);
  };

  return (
    <div className={styles.wrapper} ref={wrapperRef}>
      <div
        className={`${styles.button} ${styles[size]} ${disabled ? styles.disabled : ""} ${className ?? ""}`}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={label}
        aria-disabled={disabled}
        title={label}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={disabled ? undefined : onClick}
        onKeyDown={(e) => {
          if (!isActivationKey(e)) return;
          e.preventDefault();
          if (!disabled) onClick();
        }}
      >
        {content ?? <img src={src} alt="" />}
        {badge !== undefined && <span className={styles.badge}>{badge}</span>}
      </div>

      {menu && (
        <div
          className={styles.chevron}
          role="button"
          tabIndex={0}
          aria-label={menu.label}
          aria-haspopup="menu"
          aria-expanded={isMenuOpen}
          onClick={(e) => {
            e.stopPropagation();
            setIsMenuOpen((v) => !v);
          }}
          onKeyDown={(e) => {
            if (!isActivationKey(e)) return;
            e.preventDefault();
            e.stopPropagation();
            setIsMenuOpen((v) => !v);
          }}
        >
          <FontAwesomeIcon
            icon={faChevronDown}
            style={{ width: "8px", height: "8px" }}
          />
        </div>
      )}

      {menu && isMenuOpen && (
        <div className={styles.menu} role="menu" aria-label={menu.label}>
          {menu.items.map((item) => (
            <div
              key={item.key}
              className={`${styles.menuItem} ${item.selected ? styles.menuItemSelected : ""}`}
              role="menuitemradio"
              aria-checked={!!item.selected}
              tabIndex={0}
              onClick={() => selectItem(item)}
              onKeyDown={(e) => {
                if (!isActivationKey(e)) return;
                e.preventDefault();
                selectItem(item);
              }}
            >
              {item.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
